import { type FC, useCallback } from "react";
import { Navigate, generatePath, useParams } from "react-router-dom";

import { useFragment } from "@apollo/client";
import { Alert, Status } from "@snapchat/snap-design-system";
import { useIntl } from "react-intl";

import { useFeatureGates } from "../../../../cof-feature-gates/hooks";
import { AbsoluteRoute, type RouteParams } from "../../../../core/routes";
import {
    CofConfigId,
    ExclusiveLensStatus,
    type MyLensesOrganization,
    type ResourceContainer,
    ResourceKind,
    SurfaceType,
    Visibility,
} from "../../../../gql-generated/types";
import { Card, PageContent, PageContentSpinner, PageHeader, PageSubHeader } from "../../../../layout/components";
import { PublishModal } from "../../../../lens/components/shared";
import { LensPreviewInvalidLensPreview } from "../../../../lens-preview/components/messages.generated";
import { useLensSubmissionVisibilityConfig } from "../../../../lens-visibility/hooks";
import { organization, resourceContainer } from "../../../../shared/api/fragments";
import { useSelectedLensFolder, useSelectedLensFolderV3, useToggleState } from "../../../../shared/hooks";
import {
    useLensDetails,
    useLensDetailsBlizzardEvents,
    useLensForUpdate,
    useLensSpaceLens,
    useMergeLensForUpdateWithPresubmissionInfo,
} from "../../../hooks";
import { messages } from "../../../hooks/messages.generated";
import { validateExclusiveLens } from "../../../validation/exclusiveLensValidation";
import { LoadingContainer } from "../../layout";
import { PublishPanel, publishPanelSpacingBottom } from "../../shared";
import {
    UpdateFolderLensButton,
    UpdatePublishedLensButton,
    UploadAndPublishButton,
    UploadButton,
} from "../../shared/messages.generated";
import { LensEditDetails, LensSubmissionDisclaimer, useEditDetails } from "../shared";
import { LensDetailsHeader, LensDetailsSubHeader } from "../shared/messages.generated";

import { bannerSpacing } from "./LensSpaceLensDetails.styles";

const defaultSurfaceType = [SurfaceType.SurfaceTypeSnapchat];

export const LensSpaceLensDetails: FC = () => {
    const intl = useIntl();
    // Hook is used on both create and update pages. Update route params are superset of create, so noting them as partial here.
    const { submissionId } = useParams<Partial<RouteParams[AbsoluteRoute.SUBMIT_LENS_UPDATE]>>();

    const {
        loading: lensForUpdateLoading,
        data: lensForUpdate,
        lensId,
        error: lensForUpdateError,
    } = useLensForUpdate();

    const {
        submitLens,
        submitLoading,
        loading: submitLensLoading,
        data,
        errorMessage,
    } = useLensSpaceLens(lensId ?? "");

    const visibilityConfig = useLensSubmissionVisibilityConfig(lensForUpdate?.getLens);

    useMergeLensForUpdateWithPresubmissionInfo(
        submissionId ?? "",
        lensForUpdate?.getLens,
        data?.getPreSubmissionInfo,
        visibilityConfig.defaultVisibility,
    );

    const lensFolderDataV1 = useSelectedLensFolderV3();
    const { lensFolder: lensFolderDataV2, loading: lensFolderLoadingV2 } = useSelectedLensFolder();

    const { configs } = useFeatureGates();
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;

    const lensFolderData = isMyLensesOrgsV2Enabled ? lensFolderDataV2 : lensFolderDataV1;
    const lensFolderOrgId = isMyLensesOrgsV2Enabled ? lensFolderDataV2?.parentName : lensFolderDataV1?.organizationId;

    const { createChangeDetailsEvent } = useLensDetailsBlizzardEvents(!!lensForUpdate, "LENS_SUBMISSION_LENS_FOLDER");

    const lensEditDetails = useEditDetails(submissionId ?? lensId ?? "", createChangeDetailsEvent);
    const { changeVisibility, setLensCreatorPayoutEnrolled, lensPreviewProps, visibility, lensCreatorPayoutEnrolled } =
        lensEditDetails;
    const { isDetailsValid, detailsValidationError } = useLensDetails(
        submissionId ?? lensId ?? "",
        lensPreviewProps.validationMessage,
    );

    const handleSubmit = useCallback(() => {
        void submitLens();
    }, [submitLens]);

    const handleUploadLensToLensFolder = useCallback(() => {
        changeVisibility(Visibility.VisibilityDontPublishToSnapchat);
        setLensCreatorPayoutEnrolled(false);

        return void submitLens(Visibility.VisibilityDontPublishToSnapchat, false);
    }, [submitLens, changeVisibility, setLensCreatorPayoutEnrolled]);

    const [modalProps, showModal] = useToggleState();

    const { data: orgDataV1 } = useFragment<MyLensesOrganization>({
        fragment: organization.name,
        fragmentName: "OrganizationName",
        from: {
            __typename: "Organization",
            id: lensFolderOrgId,
        },
    });

    // Check for exclusive lens validation issues for updates
    const lensForUpdateData = lensForUpdate?.getLens.lens;
    const isExclusiveLens = lensForUpdateData?.exclusiveLensStatus === ExclusiveLensStatus.ExclusiveLensStatusExclusive;
    const isExclusiveLensPending =
        lensForUpdateData?.exclusiveLensStatus === ExclusiveLensStatus.ExclusiveLensStatusEligible;
    const isAIClip = data?.getPreSubmissionInfo.isAIClip ?? false;

    let exclusiveLensValidation = null;
    const needsExclusiveValidation =
        Boolean(lensForUpdateData && (isExclusiveLens || isExclusiveLensPending)) || isAIClip;
    if (needsExclusiveValidation && data?.getPreSubmissionInfo) {
        const { lensPreviewProps } = lensEditDetails;
        exclusiveLensValidation = validateExclusiveLens({
            hasMusic: data.getPreSubmissionInfo.hasMusic,
            hasLyrics: data.getPreSubmissionInfo.hasLyrics,
            previewMediaItems: lensPreviewProps.previewMediaItems,
            visibility,
        });
    }
    const { data: orgDataV2 } = useFragment<ResourceContainer>({
        fragment: resourceContainer,
        fragmentName: "ResourceContainerAll",
        from: {
            __typename: "ResourceContainer",
            id: lensFolderOrgId ?? "",
            kind: ResourceKind.Organizations,
        },
    });

    const lensFolderOrgName = isMyLensesOrgsV2Enabled ? orgDataV2.name : orgDataV1.name;

    const showExclusiveLensValidationAlert = exclusiveLensValidation && !exclusiveLensValidation.isValid;
    const showAIClipMismatchAlert =
        Boolean(lensForUpdateData?.isAIClip && !isAIClip) ||
        Boolean(lensForUpdateData && !lensForUpdateData.isAIClip && isAIClip);

    if (submitLensLoading || lensForUpdateLoading || lensFolderLoadingV2) {
        return <PageContentSpinner />;
    }

    // if no lens folder id - redirect back to the lens folder destination selection
    if (lensFolderData === null) {
        return (
            <Navigate
                to={generatePath(AbsoluteRoute.SUBMIT_LENS_FOLDER_SELECT, {
                    submissionId: submissionId ?? "",
                })}
            />
        );
    }

    if (lensForUpdateError) {
        throw lensForUpdateError;
    }

    return (
        <PageContent>
            {showExclusiveLensValidationAlert && (
                <Alert
                    className={bannerSpacing}
                    closable={false}
                    status="error"
                    title="Invalid Lens for Lens+ Payouts"
                    description={`This Lens is enrolled in Lens+ Payouts but does not meet the requirements: ${exclusiveLensValidation?.errorMessage}. Please update your Lens to conform to Lens+ Payouts requirements before submitting.`}
                />
            )}
            {showAIClipMismatchAlert && (
                <Alert
                    className={bannerSpacing}
                    closable={false}
                    status="error"
                    title="AI Clip Lens Mismatch"
                    description={
                        lensForUpdateData?.isAIClip
                            ? intl.formatMessage(messages.aiClipReplacementError)
                            : intl.formatMessage(messages.nonAiClipReplacementError)
                    }
                />
            )}
            {detailsValidationError && (
                <Alert
                    className={bannerSpacing}
                    closable={false}
                    status={Status.ERROR}
                    title={<LensPreviewInvalidLensPreview />}
                    description={detailsValidationError}
                />
            )}
            <PageHeader>
                <LensDetailsHeader />
            </PageHeader>
            <PageSubHeader>
                <LensDetailsSubHeader />
            </PageSubHeader>
            <LoadingContainer loading={submitLoading}>
                <Card className={publishPanelSpacingBottom}>
                    <PublishPanel
                        submitLoading={submitLoading}
                        isDetailsValid={isDetailsValid && !showExclusiveLensValidationAlert && !showAIClipMismatchAlert}
                        onLensPublish={showModal}
                        onLensUpload={handleUploadLensToLensFolder}
                        lensFolderName={lensFolderData.name}
                        organizationName={lensFolderOrgName ?? ""}
                        visibilityConfig={visibilityConfig}
                        lensForUpdate={lensForUpdate?.getLens.lens ?? undefined}
                        publishButtonText={lensForUpdate ? <UpdatePublishedLensButton /> : <UploadAndPublishButton />}
                        uploadButtonText={lensForUpdate ? <UpdateFolderLensButton /> : <UploadButton />}
                    />
                    <PublishModal
                        {...modalProps}
                        // Lens data props
                        lensName={data?.getPreSubmissionInfo.name ?? ""}
                        lensIconUrl={data?.getPreSubmissionInfo.iconUrl}
                        lensSurfaceType={data?.getPreSubmissionInfo.surfaceType ?? defaultSurfaceType}
                        lensHasMusic={data?.getPreSubmissionInfo.hasMusic}
                        lensHasLyrics={data?.getPreSubmissionInfo.hasLyrics}
                        lensId={submissionId ?? lensId ?? ""}
                        lensForUpdate={lensForUpdate?.getLens.lens ?? undefined}
                        isNewLensPublication={!lensForUpdate}
                        // Visibility props
                        visibilityConfig={visibilityConfig}
                        currentVisibility={visibility}
                        onVisibilityChange={changeVisibility}
                        // Creator payout props
                        creatorPayoutEnrolled={lensCreatorPayoutEnrolled}
                        onCreatorPayoutEnrolledChange={setLensCreatorPayoutEnrolled}
                        // Action props
                        actionButtonText={lensForUpdate ? <UpdatePublishedLensButton /> : <UploadAndPublishButton />}
                        onAction={handleSubmit}
                        actionIsPending={submitLoading}
                        actionIsDisabled={
                            !isDetailsValid ||
                            submitLoading ||
                            !!showExclusiveLensValidationAlert ||
                            !!showAIClipMismatchAlert
                        }
                        // AI Clips props
                        isAIClip={isAIClip}
                        // Other props
                        lensPreviewProps={lensPreviewProps}
                        errorMessage={errorMessage}
                        exclusiveLensStatus={
                            lensForUpdate?.getLens.lens?.exclusiveLensStatus ??
                            ExclusiveLensStatus.ExclusiveLensStatusUnset
                        }
                    />
                </Card>
                <LensEditDetails
                    lensOrSubmissionId={submissionId ?? lensId ?? ""}
                    lensEditDetails={lensEditDetails}
                    data={data}
                    lensForUpdate={lensForUpdate?.getLens.lens ?? undefined}
                    createChangeDetailsEvent={createChangeDetailsEvent}
                    errorMessage={errorMessage}
                    hasPreviewValidationError={!!detailsValidationError}
                    detailsValidationError={detailsValidationError}
                />
            </LoadingContainer>
            <LensSubmissionDisclaimer />
        </PageContent>
    );
};
